import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  BookOpen,
  Award,
  Users,
  Briefcase,
  LogIn,
  User,
  Search,
  Bell,
} from "lucide-react";
import { useUser } from "../context/UserContext";
import Logo from "../assets/logo.png";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useUser();
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/", icon: Home },
    { name: "Notes", path: "/notes", icon: BookOpen },
    { name: "Results", path: "/result", icon: Award },
    { name: "Community", path: "/community", icon: Users },
    { name: "Jobs", path: "/jobs", icon: Briefcase },
  ];

  const closeMenu = () => setIsOpen(false);

  return (
    <>
      <nav className="w-full bg-white/95 backdrop-blur-md shadow-xs border-b border-gray-200/80 fixed top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2.5 group">
                <img
                  src={Logo}
                  alt="CollegeConnect Logo"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-100 group-hover:scale-105 transition-transform shadow-sm"
                />
                <h1 className="text-xl font-extrabold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-700 bg-clip-text text-transparent hidden sm:block tracking-tight">
                  CollegeConnect
                </h1>
              </Link>
            </div>

            {/* Desktop Navigation Links with active underline indicator */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive =
                  location.pathname === link.path ||
                  (link.path === "/community" &&
                    location.pathname.startsWith("/community"));
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative flex items-center space-x-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                      isActive
                        ? "text-purple-700 bg-purple-50/70"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <Icon
                      size={17}
                      className={isActive ? "text-purple-600" : "text-gray-400"}
                    />
                    <span>{link.name}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-purple-600 rounded-full"></span>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Desktop Right Header Actions */}
            <div className="hidden md:flex items-center space-x-3">
              {/* Search Icon / Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-36 lg:w-48 pl-9 pr-3 py-1.5 bg-gray-100/80 border border-transparent rounded-full text-xs font-medium focus:bg-white focus:border-purple-300 focus:w-56 transition-all outline-none"
                />
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
              </div>

              {/* Notification Bell */}
              <button
                title="Notifications"
                className="relative p-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Bell size={19} />
                <span className="absolute top-1 right-1 w-4 h-4 bg-purple-600 text-white rounded-full text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                  3
                </span>
              </button>

              {/* Login / Profile Button */}
              {user ? (
                <Link to="/profile">
                  <div className="flex items-center space-x-2 px-3.5 py-1.5 bg-purple-50 text-purple-700 rounded-full border border-purple-200 font-semibold text-xs hover:bg-purple-100 transition-colors">
                    <User size={15} />
                    <span>{user.name || user.username}</span>
                  </div>
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center space-x-1.5 px-5 py-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-full text-xs font-bold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                >
                  <LogIn size={15} />
                  <span>Login</span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? "max-h-96 opacity-100 border-t border-gray-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-4 py-3 space-y-1 bg-white">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                    isActive
                      ? "bg-purple-50 text-purple-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={18} />
                  <span>{link.name}</span>
                </Link>
              );
            })}

            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
              {user ? (
                <Link
                  to="/profile"
                  onClick={closeMenu}
                  className="flex items-center space-x-2 text-sm font-semibold text-purple-700"
                >
                  <User size={18} />
                  <span>{user.name || user.username}</span>
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="w-full text-center py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs"
                >
                  Login to Account
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent overlap */}
      <div className="h-16"></div>
    </>
  );
}

export default Navbar;
